import { useQuery } from '@apollo/client'
import { Card, CardBody, CardHeader } from 'react-bootstrap'
import type { AiBillingScopeEnum } from '@ztechapps/common.contract'
import type { CreditAmountFormat } from '@ztechapps/ai.common'
import { useDocument, useError, useRole, useUser } from '@ztechapps/common.hook'
import { aiPermissions } from '@ztechapps/ai.common'
import { ShowIf } from '@ztechapps/webui.util'
import { CreditBalanceCard } from '../CreditBalanceCard'
import { AI_CREDIT_TRANSACTION_LIST_QUERY, MY_CREDIT_BALANCE_QUERY, currencyRateQueries } from '../graphql'
import { TopUpCreditsModal } from '../TopUpCreditsModal'
import { UsageTable } from '../UsageTable'
import { BuyCreditsModal } from './BuyCreditsModal'

const CAN_MANAGE_AI_BILLING = aiPermissions.canManageAiBilling.value

const FILTER_CATEGORY = 'aiUsageFilterCategory'

// The height above the usage table that fills its page, so its body scrolls inside itself and loads the next page.
// Measured at the payroll walk at 1920x1080: the table starts 246px down and 109px (row count, card padding,
// page padding) stay under it, 355 in all. The hauler Settings page stacks a breadcrumb band and two tab strips
// above the table instead, and its grade-types table takes 400 where payroll's takes 360 (+40), so 400 covers both.
const USAGE_TABLE_TOP_MARGIN = 400

// `billingScope` is the app's own `IAiUsageConfig.billingScope` (e.g. `payrollAiUsageConfig`), so
// the table knows whether the balance is shared by the tenant. `showBuyCredits` adds the self-service
// Buy Credits (Stripe checkout) trigger — only an app whose api binds a `checkoutRedirectUrl` and
// serves the return page can offer it, so it is opt-in.
type AiUsageProps = { billingScope: AiBillingScopeEnum; showBuyCredits?: boolean }

export const AiUsage = ({ billingScope, showBuyCredits = false }: AiUsageProps) => {
  const { data: creditData, loading: creditLoading, error: creditError } = useQuery(MY_CREDIT_BALANCE_QUERY)
  useError(creditError)
  const { user } = useUser()
  const { hasRole: canTopUp } = useRole({ roleName: CAN_MANAGE_AI_BILLING })

  const creditAccount = creditData?.myCreditBalance
  const ownerId = creditAccount?.ownerId ?? user?.tenantId
  const currency = creditAccount?.currency

  // The balance and the usage table read the same rate — resolved once here and handed down.
  const { document: currencyFormat } = useDocument<CreditAmountFormat>({
    ...currencyRateQueries.singleConfig,
    filter: { currency },
    options: { queryOptions: { skip: !currency } },
  })

  return (
    <Card>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <CreditBalanceCard
          loading={creditLoading || !!creditError}
          balance={creditAccount?.balance}
          currency={currency}
          description="Token usage and billing for AI features."
          actions={
            <ShowIf condition={showBuyCredits}>
              <BuyCreditsModal canTopUp={canTopUp} />
            </ShowIf>
          }
        />
      </CardHeader>
      <CardBody>
        <UsageTable
          query={AI_CREDIT_TRANSACTION_LIST_QUERY}
          ownerId={ownerId}
          billingScope={billingScope}
          filterCategory={FILTER_CATEGORY}
          height={{ topMargin: USAGE_TABLE_TOP_MARGIN }}
          currency={currency}
          currencyFormat={currencyFormat}
          refetchQueries={['myCreditBalance']}
          toolbarStart={
            <ShowIf condition={canTopUp}>
              <TopUpCreditsModal
                ownerId={ownerId}
                currency={currency}
                buttonText="Top up"
                refetchQueries={['myCreditBalance']}
              />
            </ShowIf>
          }
        />
      </CardBody>
    </Card>
  )
}
